// src/pages/Article.jsx - Professional Layout with Small Image
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import ApiService from '../services/apiService';

const Article = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [article, setArticle] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  async function fetchData() {
    try {
      setIsLoading(true);
      const apiResponse = await ApiService.getArticle(id);
      if (apiResponse && apiResponse.length > 0) {
        setArticle(apiResponse[0]);
      }
    } catch (error) {
      console.error('Error loading article:', error);
      setError(error.message || 'Failed to load article');
    } finally {
      setIsLoading(false);
    }
  }

  const formatDate = (dateStr) => {
    if (dateStr) {
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      } catch {
        return dateStr;
      }
    }
    return 'Date not available';
  };

  if (isLoading) {
    return (
      <div className="article-layout">
        <Navbar />
        <SearchNavigation />
        <div className="loading-fullscreen">
          <i className="bx bx-loader-alt bx-spin"></i>
          <p>Loading Article...</p>
        </div>
        <style>{`
          .article-layout { min-height: 100vh; background: #fafafa; }
          .loading-fullscreen {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 15px;
          }
          .loading-fullscreen i { font-size: 60px; color: #E91E63; }
          .loading-fullscreen p { font-size: 18px; color: #666; margin: 0; }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bx-spin { animation: spin 1s linear infinite; }
        `}</style>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-layout">
        <Navbar />
        <SearchNavigation />
        <div className="error-fullscreen">
          <i className="bx bx-error-circle"></i>
          <p>{error || 'Article not found'}</p>
          <button className="back-btn-error" onClick={() => navigate('/articles')}>
            <i className="bx bx-arrow-back"></i>
            Back to Articles
          </button>
        </div>
        <style>{`
          .article-layout { min-height: 100vh; background: #fafafa; }
          .error-fullscreen {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 15px;
            padding: 20px;
          }
          .error-fullscreen i { font-size: 60px; color: #c41849; }
          .error-fullscreen p { font-size: 18px; color: #666; margin: 0; }
          .back-btn-error {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: linear-gradient(135deg, #E91E63, #c41849);
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 10px;
          }
          .back-btn-error:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(233, 30, 99, 0.3);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="article-layout">
      <Navbar />
      <SearchNavigation />

      <div className="article-page-wrapper">
        {/* Breadcrumb / Back Button */}
        <div className="breadcrumb-container">
          <button className="back-link" onClick={() => navigate('/articles')}>
            <i className="bx bx-arrow-back"></i>
            Back to Articles
          </button>
        </div>

        {/* Main Article Card */}
        <div className="article-card-container">
          <article className="article-card">
            {/* Article Header Section */}
            <div className="article-header-section">
              {/* Subject Badge */}
              <div className="subject-badge">
                <i className="bx bx-bookmark-alt"></i>
                <span>{article.subject}</span>
              </div>

              {/* Title */}
              <h1 className="article-title">{article.title}</h1>

              {/* Excerpt */}
              {article.excerpt && (
                <p className="article-excerpt">{article.excerpt}</p>
              )}

              {/* Meta Info Bar */}
              <div className="article-meta-info">
                <div className="meta-group">
                  <div className="meta-icon">
                    <i className="bx bx-user"></i>
                  </div>
                  <div className="meta-details">
                    <span className="meta-label">Author</span>
                    <span className="meta-value">{article.author}</span>
                  </div>
                </div>

                <div className="meta-divider"></div>

                <div className="meta-group">
                  <div className="meta-icon">
                    <i className="bx bx-calendar"></i>
                  </div>
                  <div className="meta-details">
                    <span className="meta-label">Published</span>
                    <span className="meta-value">{formatDate(article.postDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Small Featured Image */}
            {article.image && (
              <div className="article-image-section">
                <img 
                  src={`https://legaleagleweb.com/articalsimage/${article.image}`}
                  alt={article.title}
                  className="article-featured-image"
                />
              </div>
            )}

            {/* Divider */}
            <div className="content-divider"></div>

            {/* Article Content */}
            <div className="article-content-section">
              <div 
                className="article-content-body"
                dangerouslySetInnerHTML={{ __html: article.description }} 
              />
            </div>

            {/* Footer */}
            <div className="article-footer-section">
              <div className="footer-gradient-line"></div>
              
              <div className="footer-content">
                <div className="author-card">
                  <div className="author-avatar">
                    <i className="bx bx-user-circle"></i>
                  </div>
                  <div className="author-info">
                    <span className="author-label">Article by</span>
                    <span className="author-name">{article.author}</span>
                  </div>
                </div>

                <button className="back-to-articles-btn" onClick={() => navigate('/articles')}>
                  <i className="bx bx-list-ul"></i>
                  View All Articles
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        * {
          box-sizing: border-box;
        }

        .article-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .article-page-wrapper {
          padding-top: 140px;
          padding-bottom: 60px;
          width: 100%;
        }

        /* ============================================
           BREADCRUMB / BACK BUTTON
        ============================================ */
        .breadcrumb-container {
          max-width: 850px;
          margin: 0 auto 24px;
          padding: 0 20px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: white;
          border: 2px solid #e8e8e8;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #555;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-link:hover {
          border-color: #E91E63;
          color: #E91E63;
          transform: translateX(-4px);
          background: #fff9f0;
        }

        .back-link i {
          font-size: 18px;
        }

        /* ============================================
           MAIN ARTICLE CARD
        ============================================ */
        .article-card-container {
          max-width: 850px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .article-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        /* ============================================
           ARTICLE HEADER SECTION
        ============================================ */
        .article-header-section {
          padding: 40px 40px 30px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.03), rgba(233, 30, 99, 0.02));
        }

        .subject-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.12), rgba(233, 30, 99, 0.08));
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          color: #E91E63;
          margin-bottom: 20px;
        }

        .subject-badge i {
          font-size: 14px;
        }

        .article-title {
          font-size: 2rem;
          font-weight: 800;
          color: #222;
          margin: 0 0 16px 0;
          line-height: 1.3;
          letter-spacing: -0.3px;
        }

        .article-excerpt {
          font-size: 1.05rem;
          color: #666;
          line-height: 1.6;
          margin: 0 0 24px 0;
        }

        .article-meta-info {
          display: flex;
          align-items: center;
          gap: 20px;
          padding-top: 20px;
          border-top: 1px solid #e8e8e8;
          flex-wrap: wrap;
        }

        .meta-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .meta-icon {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          flex-shrink: 0;
        }

        .meta-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .meta-label {
          font-size: 11px;
          color: #999;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .meta-value {
          font-size: 13px;
          color: #333;
          font-weight: 600;
        }

        .meta-divider {
          width: 1px;
          height: 32px;
          background: #e0e0e0;
        }

        /* ============================================
           SMALL FEATURED IMAGE - FIXED SIZE
        ============================================ */
        .article-image-section {
          padding: 30px 40px;
          display: flex;
          justify-content: center;
          background: #fafafa;
        }

        .article-featured-image {
          max-width: 100%;
          width: auto;
          max-height: 300px; /* SMALL IMAGE - Max 300px height */
          height: auto;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* ============================================
           CONTENT DIVIDER
        ============================================ */
        .content-divider {
          height: 2px;
          background: linear-gradient(90deg, transparent, #E91E63, #c41849, transparent);
          margin: 0 40px;
        }

        /* ============================================
           ARTICLE CONTENT SECTION
        ============================================ */
        .article-content-section {
          padding: 40px 40px 50px;
        }

        .article-content-body {
          font-size: 1rem;
          line-height: 1.75;
          color: #333;
        }

        .article-content-body p {
          margin-bottom: 1.3em;
        }

        .article-content-body h1,
        .article-content-body h2,
        .article-content-body h3,
        .article-content-body h4 {
          margin-top: 2em;
          margin-bottom: 0.8em;
          font-weight: 700;
          color: #222;
          line-height: 1.3;
        }

        .article-content-body h2 {
          font-size: 1.6rem;
          color: #E91E63;
        }

        .article-content-body h3 {
          font-size: 1.35rem;
        }

        .article-content-body h4 {
          font-size: 1.15rem;
        }

        .article-content-body ul,
        .article-content-body ol {
          margin-bottom: 1.3em;
          padding-left: 1.8em;
        }

        .article-content-body li {
          margin-bottom: 0.4em;
        }

        .article-content-body a {
          color: #E91E63;
          text-decoration: underline;
          transition: color 0.2s ease;
        }

        .article-content-body a:hover {
          color: #c41849;
        }

        .article-content-body img {
          max-width: 100%;
          height: auto;
          border-radius: 6px;
          margin: 1.3em 0;
        }

        .article-content-body blockquote {
          border-left: 3px solid #E91E63;
          padding-left: 18px;
          margin: 1.3em 0;
          font-style: italic;
          color: #666;
        }

        .article-content-body code {
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: 'Courier New', monospace;
        }

        .article-content-body pre {
          background: #f5f5f5;
          padding: 14px;
          border-radius: 6px;
          overflow-x: auto;
          margin: 1.3em 0;
        }

        .article-content-body table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.3em 0;
        }

        .article-content-body th,
        .article-content-body td {
          padding: 10px;
          border: 1px solid #e0e0e0;
          text-align: left;
        }

        .article-content-body th {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.08), rgba(233, 30, 99, 0.05));
          font-weight: 600;
        }

        /* ============================================
           ARTICLE FOOTER SECTION
        ============================================ */
        .article-footer-section {
          padding: 0 40px 40px;
        }

        .footer-gradient-line {
          height: 3px;
          background: linear-gradient(90deg, #E91E63, #c41849);
          margin-bottom: 30px;
          border-radius: 3px;
        }

        .footer-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          flex-wrap: wrap;
        }

        .author-card {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .author-avatar {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 26px;
        }

        .author-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .author-label {
          font-size: 11px;
          color: #999;
          font-weight: 500;
          text-transform: uppercase;
        }

        .author-name {
          font-size: 15px;
          color: #333;
          font-weight: 700;
        }

        .back-to-articles-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 22px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-to-articles-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.35);
        }

        .back-to-articles-btn i {
          font-size: 18px;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 768px) {
          .article-page-wrapper {
            padding-top: 130px;
            padding-bottom: 40px;
          }

          .breadcrumb-container {
            margin-bottom: 16px;
            padding: 0 15px;
          }

          .back-link {
            padding: 8px 14px;
            font-size: 13px;
          }

          .article-card-container {
            padding: 0 15px;
          }

          .article-header-section {
            padding: 30px 24px 24px;
          }

          .article-title {
            font-size: 1.6rem;
          }

          .article-excerpt {
            font-size: 0.95rem;
          }

          .article-meta-info {
            gap: 15px;
          }

          .meta-divider {
            display: none;
          }

          .article-image-section {
            padding: 24px 24px;
          }

          .article-featured-image {
            max-height: 250px; /* Smaller on mobile */
          }

          .content-divider {
            margin: 0 24px;
          }

          .article-content-section {
            padding: 30px 24px 40px;
          }

          .article-content-body {
            font-size: 0.95rem;
          }

          .article-content-body h2 {
            font-size: 1.4rem;
          }

          .article-footer-section {
            padding: 0 24px 30px;
          }

          .footer-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .back-to-articles-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .article-header-section {
            padding: 24px 20px 20px;
          }

          .article-title {
            font-size: 1.4rem;
          }

          .article-excerpt {
            font-size: 0.9rem;
          }

          .subject-badge {
            padding: 5px 12px;
            font-size: 11px;
          }

          .meta-icon {
            width: 32px;
            height: 32px;
            font-size: 16px;
          }

          .meta-value {
            font-size: 12px;
          }

          .article-image-section {
            padding: 20px;
          }

          .article-featured-image {
            max-height: 200px; /* Even smaller on very small screens */
          }

          .content-divider {
            margin: 0 20px;
          }

          .article-content-section {
            padding: 24px 20px 32px;
          }

          .article-content-body {
            font-size: 0.9rem;
          }

          .article-footer-section {
            padding: 0 20px 24px;
          }

          .author-avatar {
            width: 42px;
            height: 42px;
            font-size: 22px;
          }

          .author-name {
            font-size: 14px;
          }

          .back-to-articles-btn {
            padding: 10px 18px;
            font-size: 13px;
          }
        }
          /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 10px !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Article;